import Model from '~/models/base/Model';

/**
 * class representing Automation settings section data
 * @extends Model
 * @author rn
 */
export default class Automation extends Model {
    static _url = `${APP_URL.django}/automation/`;

    constructor() {
        super();
    }

    static answerUserchoiceFromLink(jwt) {
        const requestUrl = `${this._url}answerUserChoice/${jwt}/`;
        return this.api.get(requestUrl);
    }

    static answerUserchoiceNoAuth(jwt) {
        const requestUrl = `${this._url}answerUserChoiceNoAuth/${jwt}/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get array of automation rule objects
     * @returns {*}
     * @author rn
     */
    static getAutomationRuleList() {
        const requestUrl = `${this._url}rules/`;
        return this.api.get(requestUrl);
    }

    /**
     * Add new automation rule
     * @param params
     * @returns {*}
     * @author rn
     */
    static createNewRule(params) {
        const requestUrl = `${this._url}rules/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Add new automation rule
     * @param params
     * @returns {*}
     * @author rn
     */
    static cloneRule(ruleId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/clone/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Get details of automation rule
     * @returns {*}
     * @author rn
     */
    static getRuleDetail(ruleId) {
        const requestUrl = `${this._url}rules/${ruleId}/`;
        return this.api.get(requestUrl);
    }

    /**
     * Save rule field
     * @returns {*}
     * @author rn
     */
    static saveRuleField(ruleId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/`;
        const requestPayload = params ?? {};
        return this.api.patch(requestUrl, requestPayload);
    }

    /**
     * Delete automation rule
     * @returns {*}
     * @author rn
     */
    static deleteRule(ruleId) {
        const requestUrl = `${this._url}rules/${ruleId}/`;
        return this.api.delete(requestUrl);
    }

    /**
     * Reset automation rule
     * @returns {*}
     * @author rn
     */
    static resetRule(ruleId) {
        const requestUrl = `${this._url}rules/${ruleId}/reset/`;
        return this.api.put(requestUrl);
    }

    /**
     * Get selectable resources of automation rule
     * @returns {*}
     * @author rn
     */
    static getRuleResources(daemonId) {
        const requestUrl = `${this._url}rules/daemons/${daemonId}/resources/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get daemon params mapping action fields
     * @param daemonId
     * @returns {*}
     * @author rn
     */
    static getDaemonParamsMapping(daemonId) {
        const requestUrl = `${this._url}rules/daemons/${daemonId}/fields/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get daemon output for filter modal
     * @param daemonId
     * @returns {*}
     * @author sst
     */
    static getDaemonOutput(daemonId) {
        const requestUrl = `${this._url}rules/daemons/${daemonId}/output/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get daemon actions types and other data for new/edit action modal
     * @param daemonId
     * @returns {*}
     * @author sst
     */
    static getActionsTypes(daemonId) {
        const requestUrl = `${this._url}rules/daemons/${daemonId}/action/types/`;
        return this.api.get(requestUrl);
    }

    /**
     * Add new action to rule
     * @param ruleId
     * @param params
     * @returns {*}
     * @author sst
     */
    static addNewAction(ruleId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/action/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Edit a single rule action data
     * @param ruleId
     * @param actionId
     * @param params
     * @returns {*}
     * @author sst
     */
    static editAction(ruleId, actionId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/action/${actionId}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    /**
     * Delete single rule action
     * @param ruleId
     * @param actionId
     * @returns {*}
     * @author sst
     */
    static deleteAction(ruleId, actionId) {
        const requestUrl = `${this._url}rules/${ruleId}/action/${actionId}/`;
        return this.api.delete(requestUrl);
    }

    /**
     * Add new map data to a specific action
     * @param ruleId
     * @param actionId
     * @param params
     * @returns {*}
     * @author sst
     */
    static addNewActionMapping(ruleId, actionId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/action/${actionId}/map/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Edit single action map
     * @param ruleId
     * @param actionId
     * @param index
     * @param params
     * @returns {*}
     * @author sst
     */
    static editActionMapping(ruleId, actionId, index, params) {
        const requestUrl = `${this._url}rules/${ruleId}/action/${actionId}/map/${index}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    /**
     * Delete single action map
     * @param ruleId
     * @param actionId
     * @param index
     * @returns {*}
     * @author sst
     */
    static deleteActionMapping(ruleId, actionId, index) {
        const requestUrl = `${this._url}rules/${ruleId}/action/${actionId}/map/${index}/`;
        return this.api.delete(requestUrl);
    }

    /**
     * Save edited filter tree
     * @param ruleId
     * @param params
     * @returns {*}
     * @author sst
     */
    static saveFilter(ruleId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/filter/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    /**
     * Delete filter tree
     * @param ruleId
     * @returns {*}
     * @author sst
     */
    static deleteFilter(ruleId) {
        const requestUrl = `${this._url}rules/${ruleId}/filter/empty/`;
        return this.api.patch(requestUrl);
    }

    /**
     * Get daemon types for new rule modal
     * @returns {*}
     * @author sst
     */
    static getDaemonTypes() {
        const requestUrl = `${this._url}rules/daemons/`;
        return this.api.get(requestUrl);
    }

    /**
     * Execute rule
     * @param ruleId
     * @returns {*}
     * @author sst
     */
    static runRule(ruleId) {
        const requestUrl = `${this._url}rules/${ruleId}/run/`;
        return this.api.post(requestUrl);
    }

    /**
     * Kill rule execution
     * @param ruleId
     * @returns {*}
     * @author sst
     */
    static killRule(ruleId) {
        const requestUrl = `${this._url}rules/${ruleId}/run/`;
        return this.api.delete(requestUrl);
    }

    /**
     * Change actions order
     * @param ruleId
     * @param params
     * @returns {*}
     * @author sst
     */
    static changeActionsOrder(ruleId, params) {
        const requestUrl = `${this._url}rules/${ruleId}/action/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    /**
     * Get ubuntu automation bridges
     * @returns {*}
     * @author ap
     */
    static getBridges(param) {
        const requestUrl = `${this._url}bridgepackage/${param}/`;
        return this.api.get(requestUrl);
    }

    /**
     * Download automation bridges
     * @returns {*}
     * @author ap
     */
    static downloadBridge(filename) {
        const requestUrl = `${this._url}bridgepackage/${filename}/download/`;
        const requestPayload = { responseType: 'blob' };
        return this.api.get(requestUrl, requestPayload);
    }
}
